import { useState } from 'react'
import { useAllTeams, useAllUsers, useDeleteTeam, useDeleteGame, useDeleteUser } from '@/hooks/useSuperAdmin'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { 
  Shield, 
  Users, 
  Layers, 
  Trash2, 
  Crown,
  Gamepad2
} from 'lucide-react'

export function SuperAdmin() {
  const { data: teams, isLoading: teamsLoading } = useAllTeams()
  const { data: users, isLoading: usersLoading } = useAllUsers()
  const deleteTeam = useDeleteTeam()
  const deleteGame = useDeleteGame()
  const deleteUser = useDeleteUser()

  const [selectedTeam, setSelectedTeam] = useState<string | null>(null)

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (window.confirm(`Are you sure you want to delete team "${teamName}"? This will delete all associated games and memberships.`)) {
      try {
        await deleteTeam.mutateAsync(teamId)
      } catch (error) {
        console.error('Failed to delete team:', error)
      }
    }
  }

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (window.confirm(`Are you sure you want to delete user "${userName}"? This will remove them from all teams and games.`)) {
      try {
        await deleteUser.mutateAsync(userId)
      } catch (error) {
        console.error('Failed to delete user:', error)
        alert('Failed to delete user. They may be the last super admin.')
      }
    }
  }

  if (teamsLoading || usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Shield className="h-8 w-8 mr-3 text-purple-600" />
            Super Admin Dashboard
          </h2>
          <p className="text-gray-600 mt-1">Manage all teams and users across the platform</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Layers className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Teams</p>
              <p className="text-2xl font-bold text-gray-900">{teams?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{users?.length || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Gamepad2 className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Games</p>
              <p className="text-2xl font-bold text-gray-900">
                {teams?.reduce((sum: number, team: any) => sum + (parseInt(team.game_count) || 0), 0) || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Teams Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Layers className="h-5 w-5 mr-2" />
            All Teams ({teams?.length || 0})
          </h3>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-3">
            {teams?.map((team: any) => (
              <div key={team.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-medium text-gray-900">{team.name}</h4>
                      {team.description && (
                        <span className="text-sm text-gray-500">• {team.description}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        <span>{team.member_count} members</span>
                      </div>
                      <div className="flex items-center">
                        <Gamepad2 className="h-4 w-4 mr-1" />
                        <span>{team.game_count} games</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        Created {new Date(team.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTeam(team.id, team.name)}
                    disabled={deleteTeam.isPending}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                    title="Delete team"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Users Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            All Users ({users?.length || 0})
          </h3>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {users?.map((user: any) => (
              <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {user.nickname || user.display_name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <div className="flex items-center space-x-3 mt-2 text-xs text-gray-600">
                      <span>{user.team_count} teams</span>
                      <span>•</span>
                      <span>{user.game_count} games</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteUser(user.id, user.nickname || user.display_name)}
                    disabled={deleteUser.isPending}
                    className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    title="Delete user"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

